import { createEffect, For, onCleanup, Show } from "solid-js";
import type { TodoStore } from "../lib/store";
import type { ViewKey } from "../lib/types";

export function CommandPalette(props: {
  open: boolean;
  onClose: () => void;
  onNewTask: () => void;
  store: TodoStore;
}) {
  let dialog: HTMLElement | undefined;
  let lastFocus: Element | null = null;
  const commands: Array<{ label: string; run: () => void }> = [
    { label: "New task", run: props.onNewTask },
    ...(["today", "upcoming", "all", "completed"] as ViewKey[]).map((view) => ({
      label: `Show ${view}`,
      run: () => props.store.setView(view),
    })),
    { label: "Undo delete", run: () => void props.store.undoDelete() },
    { label: "Retry sync", run: () => void props.store.syncNow(true) },
  ];

  function run(command: () => void) {
    props.onClose();
    queueMicrotask(command);
  }

  function trapFocus(event: KeyboardEvent) {
    if (event.key === "Escape") {
      props.onClose();
      return;
    }
    if (event.key !== "Tab" || !dialog) return;

    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"))
      .filter((element) => !element.hasAttribute("disabled") && element.tabIndex >= 0);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  createEffect(() => {
    if (!props.open) return;
    lastFocus = document.activeElement;
    queueMicrotask(() => dialog?.querySelector<HTMLButtonElement>("button")?.focus());
    onCleanup(() => {
      if (lastFocus instanceof HTMLElement) lastFocus.focus();
    });
  });

  return (
    <Show when={props.open}>
      <div class="palette-backdrop" role="presentation" onMouseDown={props.onClose}>
        <section
          ref={dialog}
          class="palette"
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          tabIndex={-1}
          onMouseDown={(event) => event.stopPropagation()}
          onKeyDown={trapFocus}
        >
          <p class="eyebrow">Commands</p>
          <For each={commands}>
            {(command) => <button onClick={() => run(command.run)}>{command.label}</button>}
          </For>
        </section>
      </div>
    </Show>
  );
}
